import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery as useConvexQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { Id } from 'convex/_generated/dataModel'
import dayjs from 'dayjs'

export const Route = createFileRoute('/app/schedule/analytics')({
  component: AnalyticsPage,
})

function AnalyticsPage() {
  // Get authenticated user from Clerk/Convex
  const currentUser = useCurrentUser()

  // Show loading state while user is being fetched
  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Please sign in to view analytics</div>
      </div>
    )
  }

  return <AnalyticsPageContent userId={currentUser._id} />
}

function AnalyticsPageContent({ userId }: { userId: Id<'users'> }) {
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year' | 'all'>(
    'week'
  )

  // Calculate date range
  const getDateRange = () => {
    const now = dayjs()
    switch (timeRange) {
      case 'week':
        return { startDate: now.subtract(7, 'day').valueOf(), endDate: now.valueOf() }
      case 'month':
        return { startDate: now.subtract(30, 'day').valueOf(), endDate: now.valueOf() }
      case 'year':
        return { startDate: now.subtract(365, 'day').valueOf(), endDate: now.valueOf() }
      case 'all':
        return undefined
    }
  }

  const dateRange = getDateRange()

  // Query review statistics
  const reviewStats = useConvexQuery(api.reviews.getReviewStats, {
    userId,
    dateRange,
  })

  // Query recent reviews
  const recentReviews = useConvexQuery(api.reviews.getRecentReviews, {
    userId,
    limit: 100,
  })

  // Query all vocabulary items for overall stats
  const allVocabulary = useConvexQuery(api.vocabulary.getAllActive, {
    userId,
    includeArchived: false,
  })

  const isLoading = reviewStats === undefined || recentReviews === undefined || allVocabulary === undefined

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-white">
              Analytics
            </h1>
            <div className="flex gap-3">
              <a
                href="/app/schedule/calendar"
                className="px-4 py-2 bg-sky-blue hover:bg-terminal-green text-black rounded-lg transition-colors font-bold border-2 border-sky-blue"
              >
                [CALENDAR VIEW]
              </a>
              <a
                href="/app/schedule/analytics"
                className="px-4 py-2 bg-sky-blue hover:bg-terminal-green text-black rounded-lg transition-colors font-bold border-2 border-sky-blue"
              >
                [ANALYTICS]
              </a>
            </div>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="bg-gray-900 rounded-lg shadow-md border-2 border-sky-blue p-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">Time Range:</span>
            <div className="flex gap-2">
              {(['week', 'month', 'year', 'all'] as const).map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors border-2 ${
                    timeRange === range
                      ? 'bg-sky-blue text-black border-sky-blue'
                      : 'bg-transparent text-white border-sky-blue hover:bg-sky-blue hover:text-black'
                  }`}
                >
                  {range === 'week' && 'Last 7 Days'}
                  {range === 'month' && 'Last 30 Days'}
                  {range === 'year' && 'Last Year'}
                  {range === 'all' && 'All Time'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-gray-900 rounded-lg shadow-md border-2 border-sky-blue p-6">
            <p className="text-white">Loading analytics...</p>
          </div>
        ) : (
          <>
            {/* Overall Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <MetricCard
                title="Total Vocabulary"
                value={allVocabulary?.length ?? 0}
                icon="📚"
                color="blue"
              />
              <MetricCard
                title="Reviews Completed"
                value={reviewStats?.totalReviews ?? 0}
                icon="✅"
                color="green"
              />
              <MetricCard
                title="Success Rate"
                value={Math.round((reviewStats?.successRate ?? 0) * 100)}
                icon="🎯"
                color="purple"
                suffix="%"
              />
              <MetricCard
                title="Average Grade"
                value={reviewStats?.averageGrade?.toFixed(1) ?? '0.0'}
                icon="⭐"
                color="yellow"
              />
            </div>

            {/* Grade Distribution */}
            <div className="bg-gray-900 rounded-lg shadow-md border-2 border-sky-blue p-6 mb-6">
              <h2 className="text-xl font-semibold text-white mb-4">
                Grade Distribution
              </h2>
              <GradeDistribution distribution={reviewStats?.gradeDistribution ?? {}} />
            </div>

            {/* Recent Activity */}
            <div className="bg-gray-900 rounded-lg shadow-md border-2 border-sky-blue p-6">
              <h2 className="text-xl font-semibold text-white mb-4">
                Recent Review Activity
              </h2>
              <RecentActivityList reviews={recentReviews ?? []} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function MetricCard({
  title,
  value,
  icon,
  color,
  suffix = '',
}: {
  title: string
  value: number | string
  icon: string
  color: 'blue' | 'green' | 'purple' | 'yellow'
  suffix?: string
}) {
  const colorClasses = {
    blue: 'bg-gray-800 border-sky-blue',
    green: 'bg-gray-800 border-terminal-green',
    purple: 'bg-gray-800 border-purple-500',
    yellow: 'bg-gray-800 border-yellow-500',
  }

  const textColorClasses = {
    blue: 'text-sky-blue',
    green: 'text-terminal-green',
    purple: 'text-purple-400',
    yellow: 'text-yellow-400',
  }

  return (
    <div className={`p-6 rounded-lg border-2 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <span className={`text-3xl font-bold ${textColorClasses[color]}`}>
          {value}{suffix}
        </span>
      </div>
      <p className="text-sm text-white font-medium">{title}</p>
    </div>
  )
}

function GradeDistribution({
  distribution,
}: {
  distribution: Record<string, number>
}) {
  const grades = [
    { value: 0, label: 'Blackout', color: 'bg-red-500' },
    { value: 1, label: 'Incorrect', color: 'bg-red-400' },
    { value: 2, label: 'Hard', color: 'bg-orange-400' },
    { value: 3, label: 'Difficult', color: 'bg-yellow-400' },
    { value: 4, label: 'Good', color: 'bg-green-400' },
    { value: 5, label: 'Perfect', color: 'bg-green-600' },
  ]

  const total = Object.values(distribution).reduce((sum, count) => sum + count, 0)

  if (total === 0) {
    return (
      <p className="text-gray-400 text-center py-8">
        No reviews yet. Start reviewing to see your grade distribution!
      </p>
    )
  }

  return (
    <div className="space-y-4">
      {grades.map((grade) => {
        const count = distribution[grade.value] || 0
        const percentage = total > 0 ? (count / total) * 100 : 0

        return (
          <div key={grade.value}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-white">
                Grade {grade.value}: {grade.label}
              </span>
              <span className="text-sm text-gray-300">
                {count} ({percentage.toFixed(1)}%)
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3">
              <div
                className={`${grade.color} h-3 rounded-full transition-all duration-300`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function RecentActivityList({
  reviews,
}: {
  reviews: Array<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _id: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    vocabularyItemId: any
    grade: number
    reviewedAt: number
    reviewDuration?: number
  }>
}) {
  if (reviews.length === 0) {
    return (
      <p className="text-gray-400 text-center py-8">
        No review activity yet. Complete some reviews to see your progress!
      </p>
    )
  }

  // Show last 10 reviews
  const recentReviews = reviews.slice(0, 10)

  return (
    <div className="space-y-3">
      {recentReviews.map((review) => {
        const gradeColor = review.grade >= 4 ? 'text-terminal-green bg-terminal-green/20' : review.grade >= 3 ? 'text-yellow-400 bg-yellow-400/20' : 'text-red-400 bg-red-400/20'
        const gradeLabel = ['Blackout', 'Incorrect', 'Hard', 'Difficult', 'Good', 'Perfect'][review.grade]
        const duration = review.reviewDuration ? Math.round(review.reviewDuration / 1000) : null

        return (
          <div
            key={review._id}
            className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-sky-blue/30"
          >
            <div className="flex-1">
              <p className="text-sm text-gray-300">
                {dayjs(review.reviewedAt).format('MMM D, YYYY h:mm A')}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {duration !== null && (
                <span className="text-sm text-gray-400">{duration}s</span>
              )}
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${gradeColor}`}>
                Grade {review.grade}: {gradeLabel}
              </span>
            </div>
          </div>
        )
      })}

      {reviews.length > 10 && (
        <p className="text-sm text-gray-400 text-center pt-2">
          Showing 10 of {reviews.length} recent reviews
        </p>
      )}
    </div>
  )
}
